import { money, fmtDateTime, paymentModeLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, Share2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function Receipt({ receipt, onClose }) {
  const [width, setWidth] = useState("80");
  const [copyLabel, setCopyLabel] = useState("");
  if (!receipt) return null;

  const printCopy = (label) => {
    setCopyLabel(label);
    setTimeout(() => window.print(), 120);
  };

  const handleShare = async () => {
    const text = `Reçu CLINIT ${receipt.receipt_number}\nCommande ${receipt.order_number}\nClient: ${receipt.client_name}\nTotal: ${money(receipt.total)}\nPayé: ${money(receipt.amount_paid)}\nSolde: ${money(receipt.balance)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Reçu ${receipt.receipt_number}`, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Reçu copié dans le presse-papiers");
      }
    } catch (e) {
      console.error("Receipt share failed", e);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4 overflow-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between px-4 py-3 border-b no-print">
          <div className="flex gap-2">
            <button
              data-testid="receipt-width-58"
              onClick={() => setWidth("58")}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${width === "58" ? "bg-brand-600 text-white" : "bg-slate-100"}`}
            >
              58mm
            </button>
            <button
              data-testid="receipt-width-80"
              onClick={() => setWidth("80")}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${width === "80" ? "bg-brand-600 text-white" : "bg-slate-100"}`}
            >
              80mm
            </button>
          </div>
          <button data-testid="receipt-close-x" onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 flex justify-center max-h-[65vh] overflow-auto bg-slate-50">
          <div
            id="thermal-receipt"
            className={`bg-white ${width === "58" ? "receipt-58" : "receipt-80"} font-mono text-black p-3`}
            style={{ fontSize: width === "58" ? "10px" : "12px", lineHeight: 1.35 }}
          >
            <div className="text-center">
              <div className="font-bold" style={{ fontSize: "18px", letterSpacing: "2px" }}>CLINIT</div>
              <div>Pressing & Blanchisserie</div>
              <div>Reçu N° {receipt.receipt_number}</div>
              <div>{fmtDateTime(receipt.created_at)}</div>
              {copyLabel && <div className="font-bold" style={{ marginTop: "2px" }}>*** COPIE {copyLabel.toUpperCase()} ***</div>}
            </div>
            <div className="my-1">--------------------------------</div>
            <div>Commande: {receipt.order_number}</div>
            <div>Client: {receipt.client_name}</div>
            <div>Tel: {receipt.whatsapp || "—"}</div>
            <div className="my-1">--------------------------------</div>
            {(receipt.items || []).map((it, i) => (
              <div key={`${it.service_id || it.name}-${i}`} className="flex justify-between">
                <span>
                  {it.name} {it.billing_mode === "kilo" ? `${it.weight}kg` : `x${it.quantity}`}
                </span>
                <span>{money(it.subtotal)}</span>
              </div>
            ))}
            <div className="my-1">--------------------------------</div>
            <div className="flex justify-between"><span>Pièces</span><span>{receipt.total_pieces ?? (receipt.items || []).reduce((a, it) => a + (Number(it.quantity) || 0), 0)}</span></div>
            <div className="flex justify-between"><span>Services</span><span>{money(receipt.service_total)}</span></div>
            <div className="flex justify-between"><span>Livraison</span><span>{money(receipt.delivery_fee)}</span></div>
            {receipt.discount > 0 && (
              <div className="flex justify-between"><span>Remise</span><span>-{money(receipt.discount)}</span></div>
            )}
            <div className="flex justify-between font-bold" style={{ fontSize: "15px" }}>
              <span>TOTAL</span><span>{money(receipt.total)}</span>
            </div>
            <div className="flex justify-between"><span>Payé</span><span>{money(receipt.amount_paid)}</span></div>
            <div className="flex justify-between font-bold"><span>Solde</span><span>{money(receipt.balance)}</span></div>
            <div>Mode: {paymentModeLabel[receipt.payment_mode] || "—"}</div>
            <div>Livreur: {receipt.courier_name}</div>
            <div className="my-1">--------------------------------</div>
            <div className="text-center">Merci pour votre confiance</div>
            <div className="text-center">avec CLINIT !</div>
          </div>
        </div>

        <div className="p-4 border-t no-print space-y-2">
          <div className="text-xs text-slate-500">Imprimez chaque copie séparément :</div>
          <div className="grid grid-cols-3 gap-2">
            <Button data-testid="receipt-print-client" onClick={() => printCopy("Client")} className="bg-brand-600 hover:bg-brand-700">
              <Printer className="w-4 h-4 mr-1" /> Client
            </Button>
            <Button data-testid="receipt-print-atelier" onClick={() => printCopy("Atelier")} className="bg-brand-600 hover:bg-brand-700">
              <Printer className="w-4 h-4 mr-1" /> Atelier
            </Button>
            <Button data-testid="receipt-print-livreur" onClick={() => printCopy("Livreur")} className="bg-brand-600 hover:bg-brand-700">
              <Printer className="w-4 h-4 mr-1" /> Livreur
            </Button>
          </div>
          <div className="flex gap-2">
            <Button data-testid="receipt-share-btn" onClick={handleShare} variant="outline" className="flex-1">
              <Share2 className="w-4 h-4 mr-2" /> Partager
            </Button>
            <Button data-testid="receipt-close-btn" onClick={onClose} variant="ghost" className="flex-1">
              Fermer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
